/**
 * The chime that plays when a new support ticket arrives (t1078).
 *
 * Generated with the Web Audio API rather than shipped as a file — two short
 * notes need no asset, no extra request and no licence. Whether it plays is a
 * per-browser choice kept in localStorage, like the theme: a front-desk
 * computer and a manager's laptop reasonably want different answers.
 */

const SOUND_KEY = 'mv-ticket-alert-sound'

export function isAlertSoundEnabled(): boolean {
  try {
    return localStorage.getItem(SOUND_KEY) !== 'off'
  } catch {
    return true
  }
}

export function setAlertSoundEnabled(enabled: boolean): void {
  try {
    localStorage.setItem(SOUND_KEY, enabled ? 'on' : 'off')
  } catch {
    // Private browsing can make storage throw; the choice just won't persist.
  }
}

let context: AudioContext | null = null

function audioContext(): AudioContext | null {
  if (context) return context
  const Ctor =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
  if (!Ctor) return null
  context = new Ctor()
  return context
}

/**
 * Browsers keep audio locked until the page has had a click or keypress, so
 * the context is created and resumed on the first one. Until then a chime is
 * silently skipped rather than queued — a sound minutes late is misleading.
 * Returns the cleanup for the listeners.
 */
export function armAlertSound(): () => void {
  const unlock = () => {
    void audioContext()?.resume()
    remove()
  }
  const remove = () => {
    window.removeEventListener('pointerdown', unlock)
    window.removeEventListener('keydown', unlock)
  }
  window.addEventListener('pointerdown', unlock)
  window.addEventListener('keydown', unlock)
  return remove
}

function note(ctx: AudioContext, frequency: number, start: number, duration: number): void {
  const oscillator = ctx.createOscillator()
  const gain = ctx.createGain()
  oscillator.type = 'triangle'
  oscillator.frequency.value = frequency
  gain.gain.setValueAtTime(0.0001, start)
  gain.gain.exponentialRampToValueAtTime(0.25, start + 0.02)
  gain.gain.exponentialRampToValueAtTime(0.0001, start + duration)
  oscillator.connect(gain).connect(ctx.destination)
  oscillator.start(start)
  oscillator.stop(start + duration + 0.05)
}

/** `force` is for the settings screen's "Play a test sound", which should
 *  sound even while the sound option is off. */
export async function playAlertChime(options: { force?: boolean } = {}): Promise<void> {
  if (!options.force && !isAlertSoundEnabled()) return
  const ctx = audioContext()
  if (!ctx) return
  if (ctx.state !== 'running') {
    try {
      await ctx.resume()
    } catch {
      return
    }
  }
  if (ctx.state !== 'running') return
  const start = ctx.currentTime
  note(ctx, 880, start, 0.18)
  note(ctx, 1318.5, start + 0.16, 0.32)
}
